/**
 * Foto de la banda "quienes somos" de la portada (ADM-05).
 *
 * Una sola foto: subir otra la reemplaza en el acto (no hay nada mas que
 * guardar). Sin foto subida, la portada usa la de respaldo
 * (`FOTO_FACHADA`); volver a ella borra la subida y confirma en modal.
 *
 * En la portada la foto llena casi la mitad de la banda, de arriba abajo, y
 * se recorta para calzar: conviene una apaisada y de buena resolucion.
 */
import { useState } from 'react';

import type { ConfiguracionPortada } from '@elcarpintero/compartido';

import { FOTO_FACHADA } from '../../configuracion/foto-tienda';
import { quitarFotoTienda, subirFotoTienda } from '../../servicios/portada.servicio';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { Icono } from '../comunes/Icono';
import { Imagen } from '../comunes/Imagen';
import { Modal } from './Modal';
import { PanelAdmin } from './PanelAdmin';
import { SubidorImagen } from './SubidorImagen';

interface FotoTiendaPortadaProps {
  configuracion: ConfiguracionPortada;
  alGuardar: (configuracion: ConfiguracionPortada) => void;
}

export function FotoTiendaPortada({ configuracion, alGuardar }: FotoTiendaPortadaProps) {
  const [enviando, setEnviando] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);

  const propia = configuracion.fotoTienda;
  const foto = propia ?? FOTO_FACHADA;

  async function operar(accion: () => Promise<ConfiguracionPortada>): Promise<boolean> {
    setEnviando(true);
    setFalla(null);
    try {
      alGuardar(await accion());
      return true;
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
      return false;
    } finally {
      setEnviando(false);
    }
  }

  return (
    <PanelAdmin
      titulo="Foto de la tienda"
      bajada="La de la banda «Desde 1955», entre los productos destacados"
      extra={propia === null ? <span className="etq">Foto original</span> : null}
    >
      {falla !== null && !confirmando ? (
        <p className="acceso-error" role="alert">
          <Icono nombre="alerta" />
          <span>{falla}</span>
        </p>
      ) : null}

      <div className="foto-tienda-admin">
        <div className="foto-tienda-vista">
          <Imagen
            src={foto.url}
            alt="Foto de la tienda que muestra hoy la portada"
            ancho={foto.ancho}
            alto={foto.alto}
          />
        </div>
        <div className="foto-tienda-acciones">
          <SubidorImagen
            etiqueta="Reemplazar la foto"
            ayuda="Apaisada, de al menos 1.600 px de ancho. Se recorta para llenar la banda."
            subiendo={enviando}
            onArchivo={(archivo) => {
              void operar(() => subirFotoTienda(archivo));
            }}
          />
          {propia !== null ? (
            <button
              type="button"
              className="btn btn-sm btn-contorno"
              disabled={enviando}
              onClick={() => {
                setFalla(null);
                setConfirmando(true);
              }}
            >
              Volver a la foto original
            </button>
          ) : null}
        </div>
      </div>

      <Modal
        titulo="Volver a la foto original"
        bajada="La foto subida se borra"
        abierto={confirmando}
        onCerrar={() => {
          if (!enviando) setConfirmando(false);
        }}
      >
        {falla !== null ? (
          <p className="acceso-error" role="alert">
            <Icono nombre="alerta" />
            <span>{falla}</span>
          </p>
        ) : null}

        <p className="parrafo-panel">
          La portada vuelve a mostrar la fachada de San Diego. Para usar de nuevo la foto de hoy
          habría que subirla otra vez.
        </p>

        <div className="modal-acciones">
          <button
            type="button"
            className="btn btn-contorno"
            disabled={enviando}
            onClick={() => {
              setConfirmando(false);
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-peligro"
            disabled={enviando}
            onClick={() => {
              void operar(quitarFotoTienda).then((exito) => {
                if (exito) setConfirmando(false);
              });
            }}
          >
            {enviando ? 'Volviendo…' : 'Volver a la original'}
          </button>
        </div>
      </Modal>
    </PanelAdmin>
  );
}
